<template>
  <div>
    <navigation-top />

    <div class="org-dashboard">
      <!-- Org header -->
      <div class="org-header pa-6 pb-4 d-flex align-center">
        <div class="text-h5 font-weight-medium">{{ org?.name }}</div>
      </div>

      <!-- Recent workflows -->
      <div class="section pa-6 pt-0">
        <div class="text-subtitle-1 font-weight-medium mb-4">Recent workflows</div>
        <div class="cards-grid">
          <workflow-dialog
            v-if="hasProjects && subscriptionAccessType === 'FULL'"
            :memberships="orgMemberships"
            :set-workflow="true"
            @onsave="refreshWorkflows"
          >
            <template #activator="{ props }">
              <button type="button" v-bind="props" class="new-card d-flex flex-column align-center justify-center" data-cy="new-workflow-card" aria-label="New workflow">
                <v-icon size="32" color="grey-lighten-1">mdi-plus</v-icon>
                <div class="text-body-2 text-grey-lighten-1 mt-2">New Workflow</div>
              </button>
            </template>
          </workflow-dialog>

          <dashboard-workflow-card
            v-for="workflow in recentWorkflowsWithData"
            :key="workflow.id"
            :workflow="workflow"
          />
        </div>
        <div v-if="!hasProjects && !loadingWorkflows" class="text-body-2 text-medium-emphasis mt-2">
          Create a project first to add workflows.
        </div>
      </div>

      <!-- All projects -->
      <div class="section section--alt pa-6">
        <div class="text-subtitle-1 font-weight-medium mb-4">All projects</div>
        <div class="cards-grid">
          <settings-project-dialog :set-project="true" @project-created="onProjectCreated">
            <template #activator="{ props }">
              <button type="button" v-bind="props" class="new-card d-flex flex-column align-center justify-center" data-cy="new-project-card" aria-label="New project">
                <v-icon size="32" color="grey-lighten-1">mdi-plus</v-icon>
                <div class="text-body-2 text-grey-lighten-1 mt-2">New Project</div>
              </button>
            </template>
          </settings-project-dialog>

          <dashboard-project-card
            v-for="membership in orgMemberships"
            :key="membership.id"
            :project="membership.project"
            @set-project="onSetProject"
            @edit-project="onEditProject"
          />
        </div>
      </div>
    </div>

    <!-- Project edit dialog (imperative open) -->
    <settings-project-dialog ref="editProjectDialog" :set-project="false" />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import NavigationTop from '@/components/NavigationTop.vue'
import DashboardWorkflowCard from '@/components/DashboardWorkflowCard.vue'
import DashboardProjectCard from '@/components/DashboardProjectCard.vue'
import WorkflowDialog from '@/components/WorkflowDialog.vue'
import SettingsProjectDialog from '@/components/SettingsProjectDialog.vue'
import recentWorkflowsApi from '@/api/recentWorkflowsApi'
import client from '@/graphql/client'
import ListOrgProjects from '@/graphql/queries/ListOrgProjects'

const store = useStore()
const route = useRoute()
const router = useRouter()

// Driven by the route on /org/:orgId, or by the persisted current org when this
// page is used as the home (/dashboard).
const orgId = computed(() => route.params.orgId || store.getters['currentOrg/currentOrgId'])
const editProjectDialog = ref(null)
const recentWorkflowsWithData = ref([])
const loadingWorkflows = ref(false)

const org = computed(() =>
  store.state.organisations.organisations?.find(o => o.id === orgId.value)
)

// Projects are listed by ORG membership (listOrgProjects), not by per-project
// TeamMember rows — so every org member (incl. VIEWERs) sees all of the org's
// projects, which org membership grants them access to.
const orgProjects = ref([])

const loadOrgProjects = async () => {
  if (!orgId.value) {
    orgProjects.value = []

    return
  }
  try {
    const { listOrgProjects } = await client.query({
      operationName: 'listOrgProjects',
      query: ListOrgProjects,
      variables: { orgId: orgId.value }
    })
    orgProjects.value = listOrgProjects || []
  } catch (e) {
    orgProjects.value = []
  }
}

// Keep the { id, project } shape the cards/dialogs already expect.
const orgMemberships = computed(() =>
  orgProjects.value.map((project) => ({ id: project.id, project }))
)

const hasProjects = computed(() => orgMemberships.value.length > 0)

const orgProjectIds = computed(() => new Set(orgMemberships.value.map(m => m.project?.id)))

const subscriptionAccessType = computed(() => store.state.payments.subscriptionAccessType)

// Load org members so the top-nav org-settings gear can resolve ownership.
onMounted(async () => {
  if (!store.state.currentOrg.hydrated) {
    await store.dispatch('currentOrg/hydrate')
  }
  if (!store.state.organisations.organisations) {
    await store.dispatch('organisations/listOrganisations')
  }
  if (orgId.value && !store.state.orgMembers.membersByOrg[orgId.value]) {
    await store.dispatch('orgMembers/listOrgMembers', { orgId: orgId.value })
  }
  await loadOrgProjects()
  await refreshWorkflows()
})

const refreshWorkflows = async () => {
  loadingWorkflows.value = true
  const recent = recentWorkflowsApi.getList().filter(w => orgProjectIds.value.has(w.projectId))

  const projectById = Object.fromEntries(
    orgMemberships.value.map(m => [m.project.id, m.project.name])
  )

  const results = await Promise.all(
    recent.map(w => recentWorkflowsApi.getWorkflowSummary(w, projectById[w.projectId]))
  )
  recentWorkflowsWithData.value = results.filter(Boolean)
  loadingWorkflows.value = false
}

// Refresh workflows when org memberships load.
watch(orgMemberships, (val) => {
  if (val.length) {
    refreshWorkflows()
  }
}, { once: true })

// Re-scope when the current org changes (switching orgs while on the home).
watch(orgId, async (newId) => {
  if (!newId) {
    return
  }
  if (!store.state.orgMembers.membersByOrg[newId]) {
    await store.dispatch('orgMembers/listOrgMembers', { orgId: newId })
  }
  await loadOrgProjects()
  await refreshWorkflows()
})

const onSetProject = (projectId) => {
  router.push(`/dashboard/${projectId}`)
}

const onEditProject = (project) => {
  editProjectDialog.value?.setItem(Object.assign({}, project))
  editProjectDialog.value?.openProjectDialog()
}

const onProjectCreated = async () => {
  await loadOrgProjects()
  await refreshWorkflows()
}
</script>

<style scoped>
.org-dashboard {
  max-width: 1400px;
  margin: 0 auto;
}

.org-header {
  border-bottom: 1px solid rgba(0,0,0,0.08);
}

.section {
  background: var(--v-background-base, #faf9f8);
}

.section--alt {
  background: rgba(0,0,0,0.02);
  min-height: 300px;
}

.cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 16px;
}

.new-card {
  /* a real <button> for keyboard/screen-reader access — reset native chrome */
  width: 100%;
  font: inherit;
  color: inherit;
  background: transparent;
  height: 180px;
  border: 2px dashed rgba(0,0,0,0.15);
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}

.new-card:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
}

.new-card:hover {
  border-color: rgba(0,0,0,0.3);
  background: rgba(0,0,0,0.02);
}
</style>
